/**
 * Quién entra al sistema y qué puede hacer.
 *
 * Hasta ahora los usuarios se agregaban con SQL a mano. Eso aguanta mientras
 * son cuatro y todos se conocen; deja de aguantar cuando cada cliente tiene
 * que dar de alta a su cajera un sábado sin llamar a nadie.
 *
 * Los roles se describen por lo que permiten hacer, no por su nombre: a quien
 * está dando de alta a la cajera, la palabra «contador» no le dice nada.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Stack, Alert, Tooltip,
  Switch, FormControlLabel, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import api from '../api';
import { CampoCorreo } from './CamposContacto';
import { errorCorreo } from '../contacto';
import {
  useEsMovil, FILA, tablaAncha, ENCABEZADO, botonEncabezado, rotulo,
} from '../estilos/responsivo';
import { TARJETA, ETIQUETA_BLOQUE } from '../estilos/tarjeta';

const COLOR_ROL = {
  admin: { bg: '#ede9fe', fg: '#5b21b6' },
  contador: { bg: '#dbeafe', fg: '#1d4ed8' },
  solo_lectura: { bg: '#f1f5f9', fg: '#475569' },
};

// Colores de los bloques que explican los roles, en orden.
const TONOS_ROL = [
  { bg: '#ede9fe', fg: '#5b21b6' }, { bg: '#dbeafe', fg: '#1d4ed8' }, { bg: '#e2e8f0', fg: '#334155' },
];

const VACIO = { email: '', name: '', lastname: '', role: 'usuario', activo: true };

export default function Usuarios() {
  const esMovil = useEsMovil();
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [yo, setYo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [edicion, setEdicion] = useState(null);
  const [errorForm, setErrorForm] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [u, r, m] = await Promise.all([
        api.get('/auth/usuarios'),
        api.get('/auth/roles'),
        api.get('/auth/me').catch(() => ({ data: null })),
      ]);
      setUsuarios(u.data); setRoles(r.data); setYo(m.data); setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar la lista');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    const { id, ...cuerpo } = edicion;
    if (!cuerpo.email.trim() || !cuerpo.name.trim()) {
      setErrorForm('El correo y el nombre son obligatorios.'); return;
    }
    if (errorCorreo(cuerpo.email)) { setErrorForm(errorCorreo(cuerpo.email)); return; }
    try {
      if (id) await api.put(`/auth/usuarios/${id}`, cuerpo);
      else await api.post('/auth/usuarios', cuerpo);
      setEdicion(null); setErrorForm(''); cargar();
    } catch (e) { setErrorForm(e.response?.data?.detail || 'No se pudo guardar'); }
  };

  const desactivar = async (u) => {
    if (!window.confirm(`¿Quitarle el acceso a ${u.nombre}?`)) return;
    try { await api.delete(`/auth/usuarios/${u.id}`); cargar(); }
    catch (e) { setError(e.response?.data?.detail || 'No se pudo desactivar'); }
  };

  const nombreRol = (r) => roles.find((x) => x.valor === r)?.nombre || r;

  return (
    <Box>
      <Box sx={ENCABEZADO}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Quién entra y qué puede hacer. Se identifican con su cuenta de
            Google: aquí no se manejan contraseñas.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          {...botonEncabezado(esMovil)}
          onClick={() => { setEdicion({ ...VACIO }); setErrorForm(''); }}>
          {rotulo(esMovil, 'Nuevo', 'Nuevo usuario')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card sx={{ mb: 1 }}>
        <Box sx={{ p: TARJETA.relleno }}>
          <Typography sx={{ ...ETIQUETA_BLOQUE, color: '#64748b', mb: 0.75 }}>
            Qué puede hacer cada rol
          </Typography>
          {/* Bloques de dato sin chips: cada uno con su color (skill de
              diseño, «Fondo de la tarjeta según lo que lleva»). */}
          <Box sx={{ display: 'grid', gap: 1,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' } }}>
            {roles.map((r, i) => {
              const c = TONOS_ROL[i % TONOS_ROL.length];
              return (
                <Box key={r.valor} sx={{ p: TARJETA.relleno, bgcolor: c.bg, borderRadius: TARJETA.radio,
                                         minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: c.fg }}>{r.nombre}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#334155', lineHeight: 1.35 }}>
                    {r.descripcion}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Card>

      <Card>
        {cargando ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" sx={tablaAncha(720)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Usuario', 'Correo', 'Puede', 'Estado', ''].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11,
                      textTransform: 'uppercase', color: '#64748b' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {usuarios.map((u) => {
                  const c = COLOR_ROL[u.rol] || COLOR_ROL.solo_lectura;
                  const soyYo = yo && yo.email === u.email;
                  return (
                    <TableRow key={u.id} hover>
                      <TableCell sx={{ fontSize: 13.5, fontWeight: 600 }}>
                        {u.nombre}
                        {soyYo && (
                          <Chip size="small" label="tú" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12.5 }}>{u.email}</TableCell>
                      <TableCell>
                        <Chip size="small" label={nombreRol(u.role)}
                          sx={{ bgcolor: c.bg, color: c.fg, height: 21,
                                fontSize: 11, fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={u.activo ? 'Activo' : 'Sin acceso'}
                          color={u.activo ? 'success' : 'default'}
                          variant={u.activo ? 'filled' : 'outlined'}
                          sx={{ height: 21, fontSize: 11 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => {
                            setEdicion({ ...u, lastname: u.lastname || '' });
                            setErrorForm('');
                          }}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={soyYo ? 'No puedes quitarte el acceso a ti mismo'
                          : 'Quitar el acceso'}>
                          <span>
                            <IconButton size="small" color="error" disabled={soyYo || !u.activo}
                              onClick={() => desactivar(u)}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog fullScreen={esMovil} open={Boolean(edicion)}
        onClose={() => setEdicion(null)} fullWidth maxWidth="sm">
        <DialogTitle>{edicion?.id ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
        <DialogContent dividers>
          {errorForm && <Alert severity="error" sx={{ mb: 2 }}>{errorForm}</Alert>}
          {edicion && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <CampoCorreo etiqueta="Correo de Google" requerido fullWidth
                valor={edicion.email} disabled={Boolean(edicion.id)}
                helperText={edicion.id
                  ? 'El correo no se cambia: es con lo que la persona entra'
                  : 'Tiene que ser la cuenta de Google con la que va a entrar'}
                alCambiar={(v) => setEdicion({ ...edicion, email: v })} />
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Nombre *" fullWidth value={edicion.name}
                  onChange={(e) => setEdicion({ ...edicion, name: e.target.value })} />
                <TextField size="small" label="Apellido" fullWidth value={edicion.lastname}
                  onChange={(e) => setEdicion({ ...edicion, lastname: e.target.value })} />
              </Stack>
              <TextField select size="small" label="Qué puede hacer" fullWidth
                value={edicion.role}
                onChange={(e) => setEdicion({ ...edicion, role: e.target.value })}>
                {roles.map((r) => (
                  <MenuItem key={r.valor} value={r.valor}>
                    <Box>
                      <Typography sx={{ fontSize: 13.5 }}>{r.nombre}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                        {r.descripcion}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel control={
                <Switch checked={Boolean(edicion.activo)}
                  onChange={(e) => setEdicion({ ...edicion, activo: e.target.checked })} />
              } label="Puede entrar al sistema" />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEdicion(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
